import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { GripVertical, Pencil } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { eventApiMessage } from '@/components/events/event-errors'
import { ApiError, deleteJSON, patchJSON, postJSON } from '@/lib/api'
import { hasFieldErrors } from '@/lib/contacts/errors'
import { TicketTypeFormFields } from '@/components/events/ticket-type-form-fields'
import { useTicketTypeFormState } from '@/components/events/use-ticket-type-form-state'
import { displayMonthDay } from '@/lib/dates'

export interface EventTicketType {
  id: number
  name: string
  description: string | null
  price_cents: number
  price: string
  price_label?: string
  position: number
  sales_opens_on: string | null
  sales_closes_on: string | null
  max_per_order: number | null
  capacity: number | null
  sold_count?: number
  expected_count?: number
  checked_in_count?: number
  active: boolean
  deletable: boolean
  available: boolean
  availability: string | null
  audience?: 'everyone' | 'members' | 'specific_levels'
  membership_levels?: Array<{ id: number; name: string }>
  tax_rate?: TaxRateSummary | null
  revenue_destination?: { id: string; name: string } | null
  revenue_destination_source?:
    | 'association_default'
    | 'event_override'
    | 'ticket_type_override'
    | null
}

export interface TaxRateSummary {
  id: number
  name: string
  percentage: string
}

export function EventTicketTypes({
  eventId,
  tickets,
  taxRates = [],
  taxRatesState = 'ready',
}: {
  eventId: number
  tickets: EventTicketType[]
  taxRates?: TaxRateSummary[]
  taxRatesState?: 'loading' | 'error' | 'ready'
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = React.useState<EventTicketType | 'new' | null>(null)
  const [draggedId, setDraggedId] = React.useState<number | null>(null)
  const reorder = useMutation({
    mutationFn: (ticketTypeIds: number[]) =>
      patchJSON(`/api/v1/events/${eventId}/ticket_types/reorder`, {
        ticket_type_ids: ticketTypeIds,
      }),
    onSuccess: (event) => queryClient.setQueryData(['event', eventId], event),
  })

  const dropBefore = (targetId: number) => {
    if (draggedId === null || draggedId === targetId) return
    const withoutDragged = tickets.map(({ id }) => id).filter((id) => id !== draggedId)
    const targetIndex = withoutDragged.indexOf(targetId)
    withoutDragged.splice(targetIndex, 0, draggedId)
    reorder.mutate(withoutDragged)
    setDraggedId(null)
  }

  return (
    <section>
      <h2 className="mb-2 text-caption font-medium text-muted-foreground">Tickets</h2>
      <div className="divide-y divide-border rounded-md border border-border bg-card">
        {tickets.map((ticket) => (
          <React.Fragment key={ticket.id}>
            <div
              className={`flex items-start gap-2 px-3 py-2.5 ${ticket.active ? '' : 'opacity-55'}`}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => dropBefore(ticket.id)}
            >
              <Button
                type="button"
                variant="outline"
                draggable
                aria-label={`Move ${ticket.name}`}
                onDragStart={() => setDraggedId(ticket.id)}
                className="h-7 w-7 cursor-grab border-transparent p-0 text-muted-foreground"
              >
                <GripVertical className="h-4 w-4" aria-hidden="true" />
              </Button>
              <div className="min-w-0 flex-1">
                <p className="text-body-sm font-medium text-foreground">{ticket.name}</p>
                <p className="text-caption text-muted-foreground">
                  {ticket.capacity === null
                    ? `${ticket.sold_count ?? 0} sold`
                    : `${ticket.sold_count ?? 0} of ${ticket.capacity} sold`}
                </p>
                {ticket.description && (
                  <p className="text-caption text-muted-foreground">{ticket.description}</p>
                )}
                {salesWindowCaption(ticket) && (
                  <p className="text-caption text-muted-foreground">{salesWindowCaption(ticket)}</p>
                )}
              </div>
              <p className="w-24 shrink-0 text-right text-body-sm font-semibold tabular-nums text-foreground">
                {ticket.price_label ?? ticket.price}
              </p>
              <Button
                type="button"
                variant="outline"
                aria-label={`Edit ${ticket.name}`}
                onClick={() => setEditing(ticket)}
                className="h-8 w-8 border-transparent p-0 text-muted-foreground"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </div>
            {editing !== 'new' && editing?.id === ticket.id && (
              <TicketTypeForm
                eventId={eventId}
                ticket={editing}
                taxRates={taxRates}
                taxRatesState={taxRatesState}
                onClose={() => setEditing(null)}
              />
            )}
          </React.Fragment>
        ))}
        {editing === 'new' && (
          <TicketTypeForm
            eventId={eventId}
            taxRates={taxRates}
            taxRatesState={taxRatesState}
            onClose={() => setEditing(null)}
          />
        )}
        <div className="px-4 py-3">
          <Button
            type="button"
            variant="outline"
            aria-label="Add ticket type"
            onClick={() => setEditing('new')}
            className="h-7 border-dashed px-2 text-caption text-muted-foreground"
          >
            + Add ticket type
          </Button>
        </div>
      </div>
      {reorder.error && <Alert className="mt-2">{eventApiMessage(reorder.error)}</Alert>}
    </section>
  )
}

function TicketTypeForm({
  eventId,
  ticket,
  taxRates,
  taxRatesState,
  onClose,
}: {
  eventId: number
  ticket?: EventTicketType
  taxRates: TaxRateSummary[]
  taxRatesState: 'loading' | 'error' | 'ready'
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const form = useTicketTypeFormState(ticket)
  const lifecycle = useTicketTypeLifecycle({ eventId, ticket, onClose })
  const save = useMutation({
    mutationFn: () => {
      const body = {
        ticket_type: {
          name: form.name,
          description: form.description,
          price: form.price,
          sales_opens_on: form.salesOpensOn,
          sales_closes_on: form.salesClosesOn,
          max_per_order: form.maxPerOrder,
          capacity: form.capacity,
          audience: form.audience,
          membership_level_ids: form.audience === 'specific_levels' ? form.membershipLevelIds : [],
          tax_rate_id: form.taxRateId ? Number(form.taxRateId) : null,
          idempotency_key: form.idempotencyKey.current,
        },
      }
      return ticket
        ? patchJSON(`/api/v1/events/${eventId}/ticket_types/${ticket.id}`, body)
        : postJSON(`/api/v1/events/${eventId}/ticket_types`, body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] })
      onClose()
    },
  })

  return (
    <form
      className="grid gap-3 bg-muted/30 px-4 py-4 sm:pl-11"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <TicketTypeFormFields
        audience={form.audience}
        capacity={form.capacity}
        description={form.description}
        maxPerOrder={form.maxPerOrder}
        membershipLevelIds={form.membershipLevelIds}
        name={form.name}
        price={form.price}
        salesClosesOn={form.salesClosesOn}
        salesOpensOn={form.salesOpensOn}
        taxRateId={form.taxRateId}
        taxRates={taxRates}
        taxRatesState={taxRatesState}
        onAudienceChange={form.setAudience}
        onCapacityChange={form.setCapacity}
        onDescriptionChange={form.setDescription}
        onLevelsChange={form.setMembershipLevelIds}
        onMaxPerOrderChange={form.setMaxPerOrder}
        onNameChange={form.setName}
        onPriceChange={form.setPrice}
        onSalesClosesOnChange={form.setSalesClosesOn}
        onSalesOpensOnChange={form.setSalesOpensOn}
        onTaxRateChange={form.setTaxRateId}
      />
      {save.error && <Alert>{ticketTypeApiMessage(save.error)}</Alert>}
      {lifecycle.mutation.error && <Alert>{ticketTypeApiMessage(lifecycle.mutation.error)}</Alert>}
      <div className="flex gap-1.5">
        <Button type="submit" variant="outline" className="h-8 px-3" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save ticket type'}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-8 border-transparent px-3"
          onClick={onClose}
        >
          Cancel
        </Button>
        <TicketTypeLifecycleButton
          ticket={ticket}
          pending={lifecycle.mutation.isPending}
          onChange={lifecycle.change}
        />
      </div>
    </form>
  )
}

type LifecycleAction = 'delete' | 'deactivate' | 'reactivate'

function useTicketTypeLifecycle({
  eventId,
  ticket,
  onClose,
}: {
  eventId: number
  ticket?: EventTicketType
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (action: LifecycleAction) => {
      const path = `/api/v1/events/${eventId}/ticket_types/${ticket?.id}`
      if (action === 'delete') return deleteJSON(path)
      return patchJSON(`${path}/${action}`, {})
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] })
      onClose()
    },
  })
  const change = (action: LifecycleAction) => {
    if (action !== 'reactivate' && !window.confirm(lifecycleConfirmation(action))) return
    mutation.mutate(action)
  }
  return { mutation, change }
}

function TicketTypeLifecycleButton({
  ticket,
  pending,
  onChange,
}: {
  ticket?: EventTicketType
  pending: boolean
  onChange: (action: LifecycleAction) => void
}) {
  if (!ticket) return null
  if (!ticket.active) {
    return (
      <Button
        type="button"
        variant="outline"
        className="h-8 px-3"
        onClick={() => onChange('reactivate')}
        disabled={pending}
      >
        Reactivate ticket type
      </Button>
    )
  }

  const action = ticket.deletable ? 'delete' : 'deactivate'
  return (
    <Button
      type="button"
      variant="outline"
      className="h-8 border-transparent px-3 text-caption text-destructive hover:text-destructive"
      onClick={() => onChange(action)}
      disabled={pending}
    >
      {ticket.deletable ? 'Delete ticket type' : 'Deactivate ticket type'}
    </Button>
  )
}

function lifecycleConfirmation(action: 'delete' | 'deactivate') {
  if (action === 'delete') return 'Delete this Ticket Type? This cannot be undone.'
  return 'Deactivate this Ticket Type? It will disappear from buying surfaces.'
}

function ticketTypeApiMessage(error: Error) {
  if (error instanceof ApiError && hasFieldErrors(error.body)) {
    return Object.entries(error.body.errors)
      .flatMap(([field, messages]) => messages.map((message) => `${fieldLabel(field)} ${message}`))
      .join('. ')
  }
  return eventApiMessage(error)
}

function fieldLabel(field: string) {
  return field.charAt(0).toUpperCase() + field.slice(1).replaceAll('_', ' ')
}

function salesWindowCaption(ticket: EventTicketType) {
  if (ticket.availability) return ticket.availability
  if (ticket.sales_opens_on && ticket.sales_closes_on)
    return `Sales ${formatSalesDate(ticket.sales_opens_on)} – ${formatSalesDate(ticket.sales_closes_on)}`
  if (ticket.sales_opens_on) return `Sales from ${formatSalesDate(ticket.sales_opens_on)}`
  if (ticket.sales_closes_on) return `Sales through ${formatSalesDate(ticket.sales_closes_on)}`
  return null
}

const formatSalesDate = displayMonthDay
